import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Radar } from "lucide-react";

export const FirstDemandPrompt = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem("vsk_new_buyer") === "1" && user && user !== false && user.role === "buyer") {
        setOpen(true);
      }
    } catch { /* ignore */ }
  }, [user]);

  const dismiss = () => {
    try { localStorage.removeItem("vsk_new_buyer"); } catch { /* ignore */ }
    setOpen(false);
  };

  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/match/demands", {
        text: text.trim(),
        max_price: maxPrice ? parseFloat(maxPrice) : null,
      });
      const n = data.matches?.length || 0;
      toast.success(n > 0
        ? `Posted! ${n} matching product${n > 1 ? "s" : ""} found already — check Wanted.`
        : "Posted! We'll alert you the moment a matching product is listed.");
      dismiss();
      nav("/wanted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post — try again from the Wanted page.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="bg-[#121212] border-[#27272A] text-white sm:max-w-md" data-testid="first-demand-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radar className="h-5 w-5 text-[#007AFF]" /> What are you looking for?
          </DialogTitle>
          <DialogDescription className="text-neutral-400">
            Tell us once — our AI matches you with products instantly, and alerts you when new
            sellers list what you want.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            data-testid="first-demand-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder='e.g. "natural honey, 2 jars, delivery to Lagos"'
            className="bg-[#0A0A0A] border-[#27272A]"
            maxLength={200}
          />
          <Input
            data-testid="first-demand-maxprice"
            type="number"
            min="0"
            step="0.01"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            placeholder="Max budget in USD (optional)"
            className="bg-[#0A0A0A] border-[#27272A]"
          />
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button variant="ghost" onClick={dismiss} data-testid="first-demand-skip"
              className="text-neutral-400 hover:text-white">
              Skip for now
            </Button>
            <Button onClick={post} disabled={busy || text.trim().length < 3} data-testid="first-demand-submit"
              className="rounded-full bg-[#007AFF] hover:bg-[#1a86ff] text-white font-semibold px-6">
              {busy ? "Matching…" : "Find it for me"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
